import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { saveText } from '../../utils/download';
import { errorText } from '../../utils/errorText';
import { badge, box, button, input, label } from '../shared/styles';

/*
 * The audit ledger, for the people who audit it (iso:export: the ISO auditor
 * and the system admin). Every change with its old and new state, its reason
 * and the actor's roles at the time; paged backwards through the hash chain;
 * exported as CSV or JSON (each export is itself a ledger row); and the chain
 * re-hashed on request, naming the first row where it breaks.
 */
export const ACTIONS = ['INSERT', 'UPDATE', 'DELETE', 'RESTORE', 'SUBMIT', 'RELEASE', 'OBSOLETE',
  'TRANSITION', 'APPROVE', 'REJECT', 'PROMOTE', 'ROLLBACK', 'FREEZE', 'EXPORT'];

const EMPTY = { from: '', to: '', table: '', action: '', record: '' };

function params(filters) {
  return Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
}

function Row({ row, t }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr style={{ borderBottom: '1px solid var(--c-border)' }} data-testid={`ledger-row-${row.chain_seq}`}>
        <td>{row.chain_seq}</td>
        <td>{row.changed_at ? new Date(row.changed_at).toLocaleString() : ''}</td>
        <td>{row.username || '—'}<br /><small>{(row.actor_roles || []).join(', ')}</small></td>
        <td><span style={badge('info')}>{row.action}</span></td>
        <td>{row.table_name}<br /><small>{row.record_id}</small></td>
        <td>{row.reason || '—'}</td>
        <td><button type="button" onClick={() => setOpen(!open)} title={t('tip.ledgerDetails')} data-testid={`ledger-open-${row.chain_seq}`}>
          {open ? t('ledger_hide') : t('ledger_show')}</button></td>
      </tr>
      {open && (
        <tr data-testid={`ledger-detail-${row.chain_seq}`}>
          <td colSpan={7}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
              <div><strong>{t('ledger_old')}</strong>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(row.old_state, null, 2) ?? '—'}</pre></div>
              <div><strong>{t('ledger_new')}</strong>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(row.new_state ?? row.changes, null, 2) ?? '—'}</pre></div>
            </div>
            <small style={{ color: 'var(--c-text-muted-fg)', wordBreak: 'break-all' }}>{t('ledger_hash')}: {row.row_hash}</small>
          </td>
        </tr>
      )}
    </>
  );
}

export default function Ledger() {
  const { t } = useTranslation('quality');
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [next, setNext] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async (before = null) => {
    setError('');
    try {
      const { data } = await api.get('/iso/audit-log', { params: { ...params(applied), ...(before ? { before } : {}) } });
      setRows((prev) => (before ? [...prev, ...data.rows] : data.rows));
      setNext(data.next_before);
    } catch (e) { setError(errorText(e)); }
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const exportAs = async (format) => {
    setError('');
    try {
      const { data } = await api.get('/iso/audit-log/export', {
        params: { ...params(applied), format }, ...(format === 'csv' ? { responseType: 'text' } : {}) });
      const stamp = new Date().toISOString().slice(0, 10);
      if (format === 'csv') saveText(data, `audit-log-${stamp}.csv`, 'text/csv');
      else saveText(JSON.stringify(data, null, 2), `audit-log-${stamp}.json`, 'application/json');
    } catch (e) { setError(errorText(e)); }
  };

  const verify = async () => {
    setError('');
    try {
      const { data } = await api.get('/iso/audit-log/verify');
      setVerdict(data);
    } catch (e) { setError(errorText(e)); }
  };

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  return (
    <section data-testid="ledger">
      <p style={{ marginTop: 0, fontSize: 13 }}>{t('ledger_intro')}</p>
      <form style={{ ...box, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}
        onSubmit={(e) => { e.preventDefault(); setApplied(filters); }}>
        <label style={label}>{t('ledger_from')}<input style={input} type="date" value={filters.from} onChange={set('from')} title={t('tip.ledgerFrom')} data-testid="ledger-from" /></label>
        <label style={label}>{t('ledger_to')}<input style={input} type="date" value={filters.to} onChange={set('to')} title={t('tip.ledgerTo')} data-testid="ledger-to" /></label>
        <label style={label}>{t('ledger_table')}<input style={input} value={filters.table} onChange={set('table')} title={t('tip.ledgerTable')} data-testid="ledger-table" /></label>
        <label style={label}>{t('ledger_record')}<input style={input} value={filters.record} onChange={set('record')} title={t('tip.ledgerRecord')} data-testid="ledger-record" /></label>
        <label style={label}>{t('ledger_action')}
          <select style={input} value={filters.action} onChange={set('action')} title={t('tip.ledgerAction')} data-testid="ledger-action">
            <option value="">{t('ledger_any')}</option>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <button type="submit" style={button('var(--c-primary)')} title={t('tip.ledgerApply')} data-testid="ledger-apply">{t('ledger_apply')}</button>
        <button type="button" onClick={() => exportAs('csv')} title={t('tip.ledgerExportCsv')} data-testid="ledger-export-csv">{t('ledger_exportCsv')}</button>
        <button type="button" onClick={() => exportAs('json')} title={t('tip.ledgerExportJson')} data-testid="ledger-export-json">{t('ledger_exportJson')}</button>
        <button type="button" onClick={verify} title={t('tip.ledgerVerify')} data-testid="ledger-verify">{t('ledger_verify')}</button>
      </form>
      {verdict && (
        <p data-testid="ledger-verdict" role="status"
          style={{ color: verdict.intact ? 'var(--c-success-fg)' : 'var(--c-danger-fg)', fontWeight: 600 }}>
          {verdict.intact ? t('ledger_intact', { rows: verdict.rows })
            : t('ledger_broken', { seq: verdict.problem.chain_seq, problem: verdict.problem.problem })}
        </p>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead><tr style={{ textAlign: 'start' }}>
          {['seq', 'when', 'who', 'action', 'what', 'reason', ''].map((h) => <th key={h} style={{ textAlign: 'start' }}>{h ? t(`ledger_col_${h}`) : ''}</th>)}
        </tr></thead>
        <tbody>{rows.map((r) => <Row key={r.chain_seq} row={r} t={t} />)}</tbody>
      </table>
      {rows.length === 0 && <p>{t('ledger_empty')}</p>}
      {next && <button type="button" onClick={() => load(next)} title={t('tip.ledgerOlder')} data-testid="ledger-older">{t('ledger_older')}</button>}
    </section>
  );
}
